import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
import { partnerFormUrl } from '../../data/appTour';
interface SiteHeaderProps {
  /** Route of the current page, so its link can be marked current. */
  currentPath?: string;
  /** 'overlay' sits over the home hero; 'sticky' is the standard page bar. */
  variant?: 'overlay' | 'sticky';
  /** Show the "Partner With Us" button in the desktop bar (home page). */
  showPartnerButton?: boolean;
}
const LOGO = 'https://cdn.magicpatterns.com/uploads/xpYure8muF9bisffxXgVg2/fementity.png';
const links = [{
  href: '/',
  label: 'Home'
}, {
  href: '/get-the-app',
  label: 'Download the App'
}, {
  href: '/practitioners',
  label: 'Practitioners'
}];
const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2';

/**
 * Shared site navigation. Desktop keeps the existing inline links; on phones
 * the links move into a menu so every page stays reachable.
 */
export function SiteHeader({
  currentPath,
  variant = 'sticky',
  showPartnerButton = false
}: SiteHeaderProps) {
  const [open, setOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const isOverlay = variant === 'overlay';
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const closeOnDesktop = () => {
      if (window.innerWidth >= 768) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', closeOnDesktop);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', closeOnDesktop);
    };
  }, [open]);
  const desktopLinks = isOverlay ? links.filter(link => link.href !== '/') : links;
  return <nav aria-label="Main" className={isOverlay ? 'absolute top-0 left-0 right-0 z-50' : 'sticky top-0 left-0 right-0 z-50 bg-ivory/95 backdrop-blur-sm border-b border-charcoal/5'} data-id="element-750" data-mp="1t4n2569rvz1k">
      <div className="max-w-7xl mx-auto px-6 py-4 md:py-6 flex justify-between items-center gap-4" data-id="element-751" data-mp="1k3xamerjoutn">
        <a href="/" aria-label="Fem Entity home" className={`rounded ${focusRing}`} data-id="element-752" data-mp="1p1gohdkbz5l2">
          <img src={LOGO} alt="Fem Entity" className={isOverlay ? 'h-11 md:h-16 drop-shadow-lg' : 'h-8 md:h-10'} data-id="element-753" data-mp="1adlwy0i2779k" />
        </a>

        <div className="flex items-center gap-8" data-id="element-754" data-mp="11eiaqnnlibqu">
          <ul className="hidden md:flex items-center gap-8 text-sm font-medium text-charcoal/80" data-id="element-755" data-mp="11bmdz2nhht3z">
            {desktopLinks.map(link => {
            const current = link.href === currentPath;
            return <li key={link.href} data-id="element-756" data-mp="wecqhsjnnris">
                  <a href={link.href} aria-current={current ? 'page' : undefined} className={`rounded transition-colors duration-200 ease-out ${focusRing} ${current ? 'text-purple-700 font-semibold' : 'hover:text-purple-700'}`} data-id="element-757" data-mp="wdkwgm5pi1um">
                    {link.label}
                  </a>
                </li>;
          })}
          </ul>

          {showPartnerButton && <a href={partnerFormUrl} target="_blank" rel="noopener noreferrer" className={`hidden md:inline-flex whitespace-nowrap rounded-full border border-charcoal/20 bg-white/75 backdrop-blur-sm px-5 py-2 text-sm font-medium text-charcoal transition-colors duration-200 ease-out hover:border-purple-700 hover:text-purple-800 ${focusRing}`} data-id="element-758" data-mp="vqdnp58b9c08">
              Partner With Us
            </a>}

          <button ref={toggleRef} type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? 'Close menu' : 'Open menu'} className={`md:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-charcoal/15 bg-white/85 backdrop-blur-sm text-charcoal ${focusRing}`} data-id="element-759" data-mp="1ah4f4c5zq92t">
            {open ? <XIcon aria-hidden="true" className="h-5 w-5" data-id="element-760" data-mp="3iq0wl4bou51b" /> : <MenuIcon aria-hidden="true" className="h-5 w-5" data-id="element-761" data-mp="2l1hdxscqymh6" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-762" data-mp="2q0btmlff3ux2">
        {open && <motion.div id="mobile-menu" initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="md:hidden mx-4 mb-4 rounded-2xl border border-charcoal/10 bg-white shadow-[0_18px_50px_-24px_rgba(44,44,44,0.45)]" data-id="element-763" data-mp="2q1dfzm1vw3u3">
            <ul className="py-2" data-id="element-764" data-mp="1yriq8nvhgsk1">
              {links.map(link => {
            const current = link.href === currentPath;
            return <li key={link.href} data-id="element-765" data-mp="1pz6zf0wgj33j">
                    <a href={link.href} aria-current={current ? 'page' : undefined} onClick={() => setOpen(false)} className={`flex min-h-[48px] items-center px-5 text-base ${focusRing} focus-visible:ring-inset focus-visible:ring-offset-0 ${current ? 'font-semibold text-purple-700' : 'text-charcoal'}`} data-id="element-766" data-mp="1py5n6t3pgak8">
                      {link.label}
                    </a>
                  </li>;
          })}
            </ul>
            <div className="border-t border-charcoal/10 p-4 grid gap-3" data-id="element-767" data-mp="31dkm42fh5chz">
              <a href="/practitioners/join" onClick={() => setOpen(false)} className={`flex min-h-[48px] items-center justify-center rounded-full bg-purple-700 px-5 text-sm font-semibold text-white ${focusRing}`} data-id="element-768" data-mp="2wfj1ve6bb74x">
                Join as a Practitioner
              </a>
              <a href={partnerFormUrl} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className={`flex min-h-[48px] items-center justify-center rounded-full border border-charcoal/20 px-5 text-sm font-medium text-charcoal ${focusRing}`} data-id="element-769" data-mp="36lalw8h5fy0d">
                Partner With Us
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}